'use client';

import { Timeline as AntTimeline, Col, ConfigProvider, Flex, Row } from 'antd';
import type { TimelineItemProps } from 'antd/es/timeline';
import 'bootstrap-icons/font/bootstrap-icons.min.css';
import React, { useState } from 'react';

import { AppColors } from '../../theme';
import { EvidenceSchema } from '../evidence/evidence';
import type { TagCategory } from '../index';
import { TimelineSlip } from '../index';
import styles from './timeline.styles';

/**
 * Define la estructura de un solo paso en la línea de tiempo.
 */
export type TimelineStep = {
  /**
   * Una clave única para identificar el paso.
   */
  key: string;
  /**
   * El título o estado del paso de la línea de tiempo (p. Ej., 'Preparación', 'Entrega').
   */
  status: string;
  /**
   * Si es verdadero, el paso tendrá un botón "Ver más"/"Ver menos" para alternar la visibilidad de sus elementos.
   */
  isExpandable?: boolean;
  /**
   * Una cadena que representa unidades o un resumen, que se muestra junto al estado.
   */
  units?: string;
  /**
   * Un array de elementos que se mostrarán dentro de este paso, generalmente renderizados como componentes `TimelineSlip`.
   */
  items: Array<{
    /**
     * El número total de elementos, a menudo no utilizado en favor de `units`.
     */
    total?: number;
    /**
     * Una cadena que representa las unidades para el elemento específico.
     */
    units?: string;
    /**
     * Una etiqueta que se mostrará en el elemento.
     */
    tag?: { text: string; type?: TagCategory };
    /**
     * La fecha asociada con el pedido o artículo.
     */
    orderDate?: string;
    /**
     * Una etiqueta para el pedido.
     */
    orderLabel?: string;
    /**
     * El número o identificador del pedido.
     */
    orderNumber?: string;
    /**
     * Una URL para enlazar a más detalles.
     */
    hrefUrl?: string;
    /**
     * Un array de elementos de evidencia asociados con este elemento de la línea de tiempo.
     */
    evidences?: Array<EvidenceSchema>;
  }>;
};

/**
 * Props para el componente Timeline.
 */
export type TimelineProps = {
  /**
   * Un array de objetos `TimelineStep` que definen la estructura y el contenido de la línea de tiempo.
   */
  steps: TimelineStep[];
};

/**
 * Un componente para renderizar una línea de tiempo vertical de eventos o estados.
 * Está altamente personalizado para mostrar información de seguimiento de pedidos, con pasos expandibles.
 *
 * @param {TimelineProps} props - Las props para el componente Timeline.
 *
 * @returns {JSX.Element} El componente Timeline renderizado.
 */
const Timeline = ({ steps }: TimelineProps) => {
  const hasPreparationUnits = steps.some(
    (step) => step.status === 'Preparación' && step.units !== undefined
  );

  const initialClosedSteps = hasPreparationUnits
    ? []
    : steps.filter((step) => step.status === 'Preparación').map((step) => step.key);

  const [closedSteps, setClosedSteps] = useState<Array<string>>(initialClosedSteps);

  const toggleStep = (key: string) => {
    setClosedSteps((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const renderTimelineItem = (step: TimelineStep): TimelineItemProps => {
    const isClosed = closedSteps.includes(step.key);

    const renderExpandButton = () =>
      step.isExpandable && (
        <button
          className='font-semibold'
          onClick={() => toggleStep(step.key)}
          style={styles.timeline__itemLink}>
          {isClosed ? 'Ver más' : 'Ver menos'}
        </button>
      );

    const renderStatusHeader = () => (
      <span style={styles.timeline__itemTitle}>
        <strong className='font-semibold'>{step.status}</strong>
        {step.units !== undefined && <span style={styles.timeline__itemUnits}>{step.units}</span>}
      </span>
    );

    const renderExpandedContent = () =>
      !isClosed && (
        <Flex vertical gap={8}>
          {step.items.map((item, index) => (
            <TimelineSlip
              key={`${index}-${step.key}`}
              tag={item.tag}
              orderDate={item.orderDate ?? ''}
              quantity={item.units !== undefined ? item.units : ''}
              orderLabel={item.orderLabel ?? ''}
              orderNumber={item.orderNumber ?? ''}
              hrefUrl={item.hrefUrl}
              evidences={item.evidences}
            />
          ))}
        </Flex>
      );

    return {
      key: step.key,
      color: step.status === 'Entrega' ? AppColors.VA.SUCCESS._50_ : AppColors.VA.NEUTRALS._90_,
      children: (
        <Flex vertical style={styles.timeline__item}>
          <Col>
            <Row style={styles.timeline__itemContent} justify='space-between'>
              {renderStatusHeader()}
              {renderExpandButton()}
            </Row>
          </Col>
          {renderExpandedContent()}
        </Flex>
      )
    };
  };

  const timelineConfig = {
    theme: {
      components: {
        Timeline: {
          itemPaddingBottom: 32,
          tailColor: AppColors.VA.NEUTRALS._40_,
          tailWidth: 1,
          dotBorderWidth: 2,
          padding: 0
        }
      }
    }
  };

  return (
    <ConfigProvider theme={timelineConfig.theme}>
      <AntTimeline items={steps.map(renderTimelineItem)} style={styles.timeline} />
    </ConfigProvider>
  );
};

export default Timeline;
